import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { capitalize } from "lodash-es";
import { FluentResource } from "@fluent/bundle";
import { LocalizationProvider, Localized } from "@fluent/react";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import Alert from "@kikoff/components/src/v1/info/Alert";
import Tooltip from "@kikoff/components/src/v1/info/Tooltip";
import Select, {
  OptGroup,
  Option,
  OptionContext,
  SelectController,
  useSelectContext,
} from "@kikoff/components/src/v1/inputs/Select";
import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { msIn } from "@kikoff/utils/src/date";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import { cls, combineClasses, format } from "@kikoff/utils/src/string";

import Line from "@component/line";
import {
  fetchPaymentMethods,
  PaymentMethodType,
  paymentTypeMap,
  selectPaymentMethodBy,
  selectPaymentMethods,
  selectPaymentMethodTypeGroups,
} from "@feature/funds";
import { useOverlaysController } from "@src/overlay";
import { newReactLocalization } from "@util/l10n";

import styles from "./payment_method_selection.module.scss";

type PaymentMethodPredicate = (method: web.public_.IPaymentMethod) => boolean;

export type PaymentMethodSelectionProps = {
  className?: string;
  paymentOptions: (web.public_.IPaymentOption | PaymentMethodType)[];
  findDefault?:
    | PaymentMethodPredicate
    // Multiple pass
    | PaymentMethodPredicate[];
  firstIfNoDefault?: boolean;
  excludeTokens?: string[];
  disableMethod?(method: web.public_.IPaymentMethod): boolean | React.ReactNode;
  enrichPreview?(
    method: web.public_.IPaymentMethod,
    optionContext: OptionContext
  );
  name?: string;
  label?: string;
  labelPosition?: Select.Props["labelPosition"];
  menuContent?: {
    before?: React.ReactNode;
    after?: React.ReactNode;
  };
  groupLabels?: Partial<Record<PaymentMethodType, React.ReactNode>>;
  controller?: SelectController;
  addButtonPosition?: "group-title" | "bottom";
  withoutAutopayPrompt?: boolean;
} & (
  | // Either use change handler or form to get value
  {
      onPaymentMethodTokenChange(token): void;
    }
  | {
      name: string;
    }
);

const RESOURCES = {
  en: new FluentResource(`payment-method-pay-from = Pay from
card = Cards
bank = Bank Accounts
wallet = Wallets
payment-method-add = Add +
expedited-payment-fee = expedited payment fee
expedited-payment-fee-continued = charged for card payments. Use a bank account to avoid this fee.
`),
  es: new FluentResource(`payment-method-pay-from = Pagar desde
card = Tarjetas
bank = Cuentas bancarias
wallet = Carteras
payment-method-add = Agregar +
expedited-payment-fee = tarifa de pago acelerado
expedited-payment-fee-continued = cobrado por pagos con tarjeta. Utilice una cuenta bancaria para evitar este cargo.
`),
};

const l10n = newReactLocalization(RESOURCES);

type rpcType = (token: string) => Promise<string>;

type rpcByActionType = {
  [Key in keyof Omit<web.public_.IExternalBankAccountAction, "empty">]: rpcType;
};

const rpcByAction: rpcByActionType = {
  personaAccountVerification: (token) =>
    webRPC.ExternalBankAccounts.getPersonaVerificationUrl({
      token,
    }).then(
      handleProtoStatus({
        SUCCESS(data) {
          return data.personaVerificationUrl;
        },
        _DEFAULT: handleFailedStatus("Failed to generate verification URL."),
      })
    ),
};

export function PaymentMethodSelection({
  className,
  name,
  excludeTokens,
  menuContent = {},
  firstIfNoDefault,
  label,
  labelPosition,
  controller,
  addButtonPosition = "group-title",
  withoutAutopayPrompt = false,
  ...props
}: PaymentMethodSelectionProps) {
  if (!label) {
    label = l10n.getString("payment-method-pay-from");
  }
  const disableMethod: typeof props.disableMethod = (method) => {
    if (!method) {
      return;
    }
    const requireVerification =
      method.type === web.public_.PaymentType.ACH_BANK_ACCOUNT &&
      method.externalBankAccount.actions.some((action) =>
        Object.keys(rpcByAction).some((key) => key in action)
      );
    return (
      (props.disableMethod?.(method) ?? defaultDisableMethod(method)) ||
      requireVerification
    );
  };

  const dispatch = useDispatch();
  const overlays = useOverlaysController();
  const store = useStore();

  const paymentMethods = useSelector(selectPaymentMethods());
  const paymentMethodGroups = useSelector(
    selectPaymentMethodTypeGroups({ excludeTokens })
  );

  const paymentOptions = useMemo(
    () =>
      props.paymentOptions.map((paymentOption) => {
        if (typeof paymentOption === "string") {
          return {
            paymentType: (paymentTypeMap[
              paymentOption
            ] as unknown) as web.public_.PaymentType,
            percentFee: 0,
            fixedFeeCents: 0,
          };
        }

        return paymentOption;
      }),
    [props.paymentOptions]
  );

  const paymentOptionsWithoutFee = useMemo(
    () =>
      new Set(
        paymentOptions
          .filter(
            ({ percentFee, fixedFeeCents }) =>
              percentFee === 0 && fixedFeeCents === 0
          )
          .map(({ paymentType }) => paymentType)
      ),
    [paymentOptions]
  );

  const findDefault: typeof props.findDefault = useMemo(
    () => [
      ...[props.findDefault].flat(),
      (method) => paymentOptionsWithoutFee.has(method.type),
    ],
    [props.findDefault]
  );

  const findMethodToken = (predicate: PaymentMethodPredicate) =>
    paymentMethods?.find(
      (method) =>
        paymentOptions
          .map(({ paymentType }) => paymentType)
          .includes(method.type) &&
        !disableMethod(method) &&
        predicate(method)
    )?.token;

  const getDefault = () => {
    if (typeof findDefault === "function") {
      const found = findMethodToken(findDefault);
      if (found) return found;
    } else {
      for (const predicate of findDefault) {
        if (!predicate) continue;

        const found = findMethodToken(predicate);
        if (found) return found;
      }
    }

    if (firstIfNoDefault) return findMethodToken(() => true);
  };

  const feeByPaymentType = useMemo(
    () =>
      Object.fromEntries(
        paymentOptions.map(({ paymentType, ...fees }) => [paymentType, fees])
      ),
    [paymentOptions]
  );

  const enrichPreview: typeof props.enrichPreview =
    props.enrichPreview ||
    ((method, { isPreview }) => {
      const { percentFee, fixedFeeCents } = feeByPaymentType[method.type];
      const hasFees = percentFee > 0 || fixedFeeCents > 0;

      return (
        isPreview &&
        hasFees && (
          <span className="color:error">
            {percentFee > 0 && `(+${percentFee}%) `}
            {fixedFeeCents > 0 && `(+${format.money(fixedFeeCents)})`}
          </span>
        )
      );
    });

  const groupLabels: typeof props.groupLabels = useMemo(() => {
    return {
      card: l10n.getString("card"),
      bank: l10n.getString("bank"),
      wallet: l10n.getString("wallet"),
      cashappStripe: "Cash App Pay",
      ...Object.fromEntries(
        paymentOptions
          .filter(
            ({ percentFee, fixedFeeCents }) =>
              percentFee > 0 || fixedFeeCents > 0
          )
          .map(({ paymentType, percentFee, fixedFeeCents }) => [
            paymentTypeMap[paymentType],
            <GroupWithFeesAndNote
              type={paymentTypeMap[paymentType] as PaymentMethodType}
              percentFee={percentFee}
              fixedFeeCents={fixedFeeCents}
            />,
          ])
      ),
      ...props.groupLabels,
    };
  }, paymentOptions);

  const [value, setValue] = useState(getDefault);

  useEffect(() => {
    if ("onPaymentMethodTokenChange" in props)
      props.onPaymentMethodTokenChange(value);
  }, [value]);

  // Re-find default if selected payment method is removed
  useEffect(() => {
    if (!findMethodToken(({ token }) => token === value))
      setValue(getDefault());
  }, [paymentMethods]);

  useEffect(() => {
    if (paymentMethods) setValue(getDefault());
    else dispatch(fetchPaymentMethods());
  }, [!!paymentMethods]);

  return (
    <div
      className={combineClasses(
        styles["grouped-payment-method-selection"],
        styles[`label-position_${labelPosition}`],
        className
      )}
    >
      <LocalizationProvider l10n={l10n}>
        <Select
          label={label}
          labelPosition={labelPosition}
          value={value}
          name={name}
          onChange={(val) => setValue(val)}
          controller={controller}
        >
          {menuContent.before}
          {paymentOptions.map(({ paymentType }) => (
            <OptGroup
              key={paymentType}
              label={
                addButtonPosition === "bottom" && paymentOptions.length === 1
                  ? undefined
                  : ({ hideMenu }) => (
                      <div className={styles["group-label"]}>
                        <header>
                          {groupLabels[paymentTypeMap[paymentType]]}
                        </header>
                        {paymentType !==
                          web.public_.PaymentType.CASHAPP_STRIPE &&
                          paymentType !==
                            web.public_.PaymentType.WALLET_PAYMENT_TYPE && (
                            <ContainerButton
                              className={styles.add}
                              data-cy={`add-${paymentTypeMap[paymentType]}`}
                              onClick={() => {
                                hideMenu();
                                overlays
                                  .push("payments/add_payment_method", {
                                    type: paymentTypeMap[
                                      paymentType
                                    ] as PaymentMethodType,
                                    withoutAutopayPrompt,
                                  })
                                  .then((token) => {
                                    if (
                                      !!token &&
                                      !disableMethod(
                                        selectPaymentMethodBy(
                                          "token",
                                          token
                                        )(store.getState())
                                      )
                                    )
                                      setValue(token);
                                  });
                              }}
                            >
                              <Localized id="payment-method-add">
                                Add +
                              </Localized>
                            </ContainerButton>
                          )}
                      </div>
                    )
              }
            >
              {paymentMethodGroups[paymentType].map((method) => {
                const disabled = disableMethod(method);
                const hasDisableReason = disabled && disabled !== true;
                const disabledStyleNone =
                  paymentType === web.public_.PaymentType.ACH_BANK_ACCOUNT;

                return (
                  <Option
                    className={combineClasses(
                      styles.option,
                      styles[paymentTypeMap[paymentType]]
                    )}
                    value={method.token}
                    key={method.token}
                    disabled={!!disabled}
                    disabledStyle={disabledStyleNone ? "none" : "transparent"}
                  >
                    {(optionContext) => {
                      const contentAfter = (
                        <>
                          {hasDisableReason && (
                            <Tooltip content={disabled}></Tooltip>
                          )}
                          {enrichPreview(method, optionContext)}
                        </>
                      );

                      return (
                        <>
                          {{
                            [web.public_.PaymentType.ACH_BANK_ACCOUNT]: (
                              <BankPaymentMethod
                                isPreview={optionContext.isPreview}
                                labelPosition={labelPosition}
                                paymentMethod={method}
                                disabled={!!disabled}
                                contentAfter={contentAfter}
                              />
                            ),
                          }[paymentType] || (
                            <>
                              {method.description} {contentAfter}
                            </>
                          )}
                        </>
                      );
                    }}
                  </Option>
                );
              })}
            </OptGroup>
          ))}
          {addButtonPosition === "bottom" && paymentOptions.length === 1 && (
            <BottomAddPaymentMethod
              paymentType={paymentOptions[0].paymentType}
              setValue={setValue}
              disableMethod={disableMethod}
            />
          )}
          {menuContent.after}
        </Select>
      </LocalizationProvider>
    </div>
  );
}

const defaultDisableMethod: PaymentMethodSelectionProps["disableMethod"] = (
  method
) => {
  const { Status } = web.public_.ExternalBankAccount;

  if (method.type === web.public_.PaymentType.CARD) {
    if (method.status === "frozen" || method.status === "disabled")
      return "Card has been disabled for too many failures. Please visit your accounts page to resolve the issue.";
    return false;
  }

  return (
    {
      [Status.PENDING_MANUAL_VERIFICATION]:
        "This account requires manual verification before you can use it.",
      [Status.PENDING_AUTOMATIC_VERIFICATION]:
        "Your account is still being verified, this process must complete before you can use it.",
    }[method.externalBankAccount?.status] || false
  );
};

type GroupWithFeesAndNoteProps = {
  type: PaymentMethodType;
  percentFee?: number;
  fixedFeeCents?: number;
};
function GroupWithFeesAndNote({
  type,
  percentFee,
  fixedFeeCents,
}: GroupWithFeesAndNoteProps) {
  return (
    <>
      {l10n.getString(type)}
      <div className={styles.note}>
        <span className="color:error">
          {percentFee > 0 && `${percentFee}% `}
          {fixedFeeCents > 0 && `${format.money(fixedFeeCents)} `}
          {type === "card" && l10n.getString("expedited-payment-fee")}
        </span>{" "}
        {type === "card"
          ? l10n.getString("expedited-payment-fee-continued")
          : "This payment type has additional fees."}
      </div>
    </>
  );
}

export const findAutopayDefault = (accountToken: string) => (method) =>
  method.affiliations.some(
    ({ token, usage }) =>
      token === accountToken &&
      usage === web.public_.PaymentMethod.Usage.AUTOPAY
  );

type BankPaymentMethodProps = {
  isPreview: boolean;
  labelPosition: PaymentMethodSelectionProps["labelPosition"];
  paymentMethod: web.public_.IPaymentMethod;
  disabled?: boolean;
  contentAfter: React.ReactNode;
};

function BankPaymentMethod({
  isPreview,
  labelPosition,
  paymentMethod: { description, externalBankAccount },
  disabled = false,
  contentAfter,
}: BankPaymentMethodProps) {
  const action = externalBankAccount.actions.find((action) =>
    Object.keys(rpcByAction).find((key) => key in action)
  );

  return (
    <>
      <div
        className={cls(
          styles["bank-payment-method"],
          disabled && styles.disabled
        )}
      >
        {labelPosition === "inline" ? (
          <div className="text:small">
            <div>{externalBankAccount.institutionName}</div>
            <div className="color:moderate">
              {`${capitalize(externalBankAccount.subtype)} ••••${
                externalBankAccount.lastFour
              }`}
            </div>
          </div>
        ) : (
          description
        )}
        <div className="ml-1">{contentAfter}</div>
      </div>

      {!isPreview &&
        action &&
        (() => {
          const actionType = Object.keys(rpcByAction).find(
            (key) => key in action
          ) as keyof rpcByActionType;
          const actionContent = action[actionType];

          return (
            <Alert type="warning" className="mt-2 mb-1.5">
              <div className="text:small+ mb-1">{actionContent.title}</div>
              <div className="text:small mb-2">{actionContent.body}</div>
              <ActionCta
                token={externalBankAccount.token}
                ctaText={actionContent.ctaText}
                rpc={rpcByAction[actionType]}
              />
            </Alert>
          );
        })()}
    </>
  );
}

type ActionCtaProps = {
  token: string;
  ctaText: string;
  rpc: rpcType;
};

function ActionCta({ token, ctaText, rpc }: ActionCtaProps) {
  const { hideMenu } = useSelectContext();
  const overlays = useOverlaysController();
  const dispatch = useDispatch();

  return (
    <KButton
      variant="black"
      size="small"
      onClick={async () => {
        const personaVerificationUrl = await rpc(token);
        hideMenu();
        await overlays.push("identity_verification/persona_upload", {
          link: personaVerificationUrl,
        });
        setTimeout(() => {
          dispatch(fetchPaymentMethods());
        }, 2 * msIn.second);
      }}
    >
      {ctaText}
    </KButton>
  );
}

type BottomAddPaymentMethodProps = {
  paymentType: web.public_.PaymentType;
  setValue: (string) => void;
  disableMethod: PaymentMethodSelectionProps["disableMethod"];
  withoutAutopayPrompt?: boolean;
};

function BottomAddPaymentMethod({
  paymentType,
  setValue,
  disableMethod,
  withoutAutopayPrompt = false,
}: BottomAddPaymentMethodProps) {
  const overlays = useOverlaysController();
  const { hideMenu } = useSelectContext();
  const store = useStore();

  if (paymentType === web.public_.PaymentType.WALLET_PAYMENT_TYPE) {
    return;
  }

  return (
    <>
      <Line />
      <ContainerButton
        className={styles["bottom-add"]}
        onClick={() => {
          hideMenu();
          overlays
            .push("payments/add_payment_method", {
              type: paymentTypeMap[paymentType] as PaymentMethodType,
              withoutAutopayPrompt,
            })
            .then((token) => {
              if (
                !disableMethod(
                  selectPaymentMethodBy("token", token)(store.getState())
                )
              ) {
                setValue(token);
              }
            });
        }}
      >
        + Add{" "}
        {
          {
            [web.public_.PaymentType.ACH_BANK_ACCOUNT]: "bank account",
          }[paymentType]
        }
      </ContainerButton>
    </>
  );
}
